import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  Snackbar,
  Chip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ---- Generic tile wrapper (same styling as in AWB page)
const DashboardTile = ({ title, actions, children }) => (
  <Paper
    elevation={2}
    sx={{
      p: 2,
      borderRadius: 2,
      minHeight: 320,
      display: "flex",
      flexDirection: "column",
      gap: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", gap: 1 }}>{actions}</Box>
    </Box>
    <Divider />
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {children}
    </Box>
  </Paper>
);

const severityColor = (sev) => {
  switch ((sev || "").toLowerCase()) {
    case "high":
      return "#f44336";
    case "medium":
      return "#ff9800";
    case "low":
      return "#2196f3";
    default:
      return "#9e9e9e";
  }
};
const severityIcon = (sev) => {
  const s = (sev || "").toLowerCase();
  if (s === "high") return <ErrorOutlineIcon fontSize="small" />;
  if (s === "medium") return <WarningAmberIcon fontSize="small" />;
  return <InfoOutlinedIcon fontSize="small" />;
};

// ---- Hook pentru taskurile de Intrări (se vor citi din backend)
function useIntrariTasks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const endpoint =
    "https://server-9p4hx.kinsta.app/api/ai/luxura-ai-intrari/intrari/issues";

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const rsp = await fetch(endpoint);
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      const json = await rsp.json();
      const list = json.tasks || json.items || [];
      const mapped = list.map((t) => ({
        id: t.id || t.nr_contract || t.nr_jurnal_special || t.jira_key,
        jira_key: t.jira_key,
        nr_jurnal_special: t.nr_jurnal_special,
        message: t.message || t.title || "Task fără descriere",
        severity: t.severity || "medium",
        detected_at: Date.now(),
        created_at: t.created_at || t.created_date,
        actions:
          t.jira_url || t.jira_key
            ? [
                {
                  label: "Deschide în Jira",
                  href:
                    t.jira_url ||
                    `https://luxura.atlassian.net/browse/${t.jira_key}`,
                },
              ]
            : [],
      }));
      setItems(mapped);
    } catch (e) {
      setError(e.message || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { items, loading, error, refresh };
}

function useVanzariIssues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // backend endpoint for vanzari issues
  const endpoint =
    "https://server-9p4hx.kinsta.app/api/ai/luxura-ai-intrari-vanzari/vanzari/issues";

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const rsp = await fetch(endpoint);
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      const json = await rsp.json();
      const list = json.items || [];
      const mapped = list.map((t) => ({
        id: t.issue_id,
        jira_key: t.issue_id,
        message: t.message || `Vanzarea din JIRA ${t.issue_id}`,
        severity: t.severity || "high",
        actions: [
          ...(t.jira_url
            ? [{ label: "Deschide în Jira", href: t.jira_url }]
            : []),
          ...(t.order_url
            ? [
                {
                  label: `Comanda ${t.order_id || ""}`.trim(),
                  href: t.order_url,
                },
              ]
            : []),
        ],
        detected_at: Date.now(),
      }));
      setItems(mapped);
    } catch (e) {
      setError(e.message || "Eroare la încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { items, loading, error, refresh };
}

function WarningItem({ issue }) {
  const {
    id,
    message,
    severity = "low",
    detected_at,
    actions = [],
    created_at,
  } = issue;
  return (
    <Box
      sx={{
        borderLeft: `4px solid ${severityColor(severity)}`,
        backgroundColor: "background.paper",
        borderRadius: 1,
        p: 1.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
      }}
    >
      <Box sx={{ mt: "2px" }}>{severityIcon(severity)}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            cursor: actions[0]?.href ? "pointer" : "default",
            color: actions[0]?.href ? "primary.main" : "text.primary",
          }}
          onClick={() =>
            actions[0]?.href && window.open(actions[0].href, "_blank")
          }
        >
          {message}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {`ID #${id || "—"} • `}
          {created_at
            ? new Date(created_at).toLocaleString()
            : detected_at
              ? new Date(detected_at).toLocaleString()
              : "detectat"}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, ml: 1 }}>
        {issue.onDetect && (
          <Button size="small" variant="outlined" onClick={issue.onDetect}>
            Detectează
          </Button>
        )}
        {issue.onApply && (
          <Button size="small" variant="contained" onClick={issue.onApply}>
            Aplică
          </Button>
        )}
        {/* Extra action buttons (e.g., Jira/Comandă) */}
        {Array.isArray(actions) && actions.length > 0 && (
          <Stack direction="column" spacing={0.5}>
            {actions.slice(0, 3).map((a, i) => (
              <Button
                key={`${id}-act-${i}`}
                size="small"
                variant="text"
                onClick={() => a.href && window.open(a.href, "_blank")}
              >
                {a.label || "Deschide"}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

const API_BASE =
  "https://server-9p4hx.kinsta.app/api/ai/luxura-ai-intrari-autofix/issue";

export default function LuxuraAI_Intrari() {
  const { items, loading, error, refresh } = useIntrariTasks();
  const vanzari = useVanzariIssues();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIssue, setPreviewIssue] = useState(null);
  const [detected, setDetected] = useState(null);
  const [detectedMeta, setDetectedMeta] = useState(null);
  const [working, setWorking] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getValuesForDocType = (docType) => {
    const list = Array.isArray(detectedMeta?.files_info)
      ? detectedMeta.files_info
      : [];
    const entry = list.find((f) => f.doc_type === docType);
    return entry?.values || {};
  };

  const mergedEntries = () => {
    const list = Array.isArray(detectedMeta?.files_info)
      ? detectedMeta.files_info
      : [];
    const byType = list.reduce((acc, f) => {
      const vals = f.values || {};
      const clean = Object.fromEntries(
        Object.entries(vals).filter(([_, v]) => v != null && v !== "")
      );
      if (!acc[f.doc_type]) acc[f.doc_type] = { files: [], values: {} };
      acc[f.doc_type].files.push(f.filename);
      acc[f.doc_type].values = { ...acc[f.doc_type].values, ...clean };
      return acc;
    }, {});
    return byType;
  };

  const handleDetect = async (issue) => {
    const key = issue.jira_key;
    if (!key) {
      setSnack({
        open: true,
        message: "Lipsește jira_key pentru acest task.",
        severity: "error",
      });
      return;
    }
    setWorking(true);
    try {
      const rsp = await fetch(
        `${API_BASE}/${encodeURIComponent(key)}/extract`,
        { method: "POST" }
      );
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      const json = await rsp.json();
      setPreviewIssue(issue);
      setDetected(json.detected || {});
      setDetectedMeta({
        primary_source: json.primary_source || "unknown",
        files_info: json.files_info || [],
      });
      setPreviewOpen(true);
    } catch (e) {
      setSnack({
        open: true,
        message: e.message || "Eroare la detectare",
        severity: "error",
      });
    } finally {
      setWorking(false);
    }
  };

  const handleApply = async (overrideValues = null) => {
    if (!previewIssue || !previewIssue.jira_key) return;
    setWorking(true);
    try {
      const rsp = await fetch(
        `${API_BASE}/${encodeURIComponent(previewIssue.jira_key)}/apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ values: overrideValues || detected || {} }),
        }
      );
      if (!rsp.ok) throw new Error(`HTTP ${rsp.status}`);
      const json = await rsp.json();
      setSnack({
        open: true,
        message: `Actualizat: ${Array.isArray(json.updated) ? json.updated.join(", ") : "OK"}`,
        severity: "success",
      });
      setPreviewOpen(false);
      setDetected(null);
      setDetectedMeta(null);
      setPreviewIssue(null);
      // opțional: reîmprospătează lista
      refresh();
    } catch (e) {
      setSnack({
        open: true,
        message: e.message || "Eroare la aplicare",
        severity: "error",
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        {/* Stânga: Taskuri Intrări din backend */}
        <Grid item xs={12} md={6}>
          <DashboardTile
            title="Taskuri Intrări"
            actions={
              <Tooltip title="Reîmprospătează lista">
                <span>
                  <IconButton onClick={refresh} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            }
          >
            <Box sx={{ position: "relative", flex: 1, overflow: "auto" }}>
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              {error && (
                <Box sx={{ p: 2 }}>
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                </Box>
              )}
              {!loading && !error && items.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Niciun task de afișat.
                  </Typography>
                </Box>
              )}
              {!loading && !error && items.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.2,
                    p: 1,
                  }}
                >
                  {items.map((issue, idx) => (
                    <WarningItem
                      key={`${issue.id}-${idx}`}
                      issue={{
                        ...issue,
                        onDetect: () => handleDetect(issue),
                        onApply: () => {
                          setPreviewIssue(issue);
                          setDetected(detected || {});
                          setPreviewOpen(true);
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </DashboardTile>
        </Grid>

        {/* Dreapta: Vanzari Issues tile */}
        <Grid item xs={12} md={6}>
          <DashboardTile
            title="Vanzari Issues"
            actions={
              <Tooltip title="Reîmprospătează lista">
                <span>
                  <IconButton
                    onClick={vanzari.refresh}
                    disabled={vanzari.loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            }
          >
            <Box sx={{ position: "relative", flex: 1, overflow: "auto" }}>
              {vanzari.loading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
              {vanzari.error && (
                <Box sx={{ p: 2 }}>
                  <Typography color="error" variant="body2">
                    {vanzari.error}
                  </Typography>
                </Box>
              )}
              {!vanzari.loading &&
                !vanzari.error &&
                vanzari.items.length === 0 && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Niciun issue de vânzări.
                    </Typography>
                  </Box>
                )}
              {!vanzari.loading &&
                !vanzari.error &&
                vanzari.items.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.2,
                      p: 1,
                    }}
                  >
                    {vanzari.items.map((issue, idx) => (
                      <WarningItem key={`${issue.id}-${idx}`} issue={issue} />
                    ))}
                  </Box>
                )}
            </Box>
          </DashboardTile>
        </Grid>
      </Grid>

      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setDetectedMeta(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Valori detectate din PDF{" "}
          {previewIssue?.jira_key ? `(${previewIssue.jira_key})` : ""}
          {detectedMeta?.primary_source && (
            <Chip
              size="small"
              sx={{ ml: 1 }}
              label={`Sursă: ${detectedMeta.primary_source === "act_aditional" ? "Act Adițional" : detectedMeta.primary_source}`}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {working && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 2,
              }}
            >
              <CircularProgress size={22} />
            </Box>
          )}
          {!working && (
            <>
              {Array.isArray(detectedMeta?.files_info) &&
                detectedMeta.files_info.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                    >
                      Fișiere analizate
                    </Typography>
                    <Stack spacing={0.5}>
                      {detectedMeta.files_info.map((f, i) => (
                        <Stack
                          key={`${f.filename}-${i}`}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Chip size="small" label={f.doc_type || "unknown"} />
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {f.filename}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
              >
                {(() => {
                  const byType = mergedEntries();
                  const sections = [
                    { key: "act_aditional", title: "Act Adițional" },
                    { key: "contract", title: "Contract" },
                  ];
                  const hasAny = Object.keys(byType).some(
                    (k) => Object.keys(byType[k]?.values || {}).length > 0
                  );
                  if (!hasAny)
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Nu s-au găsit valori în atașamente.
                      </Typography>
                    );
                  return sections.map((sec) => {
                    const vals = byType[sec.key]?.values || {};
                    const files = byType[sec.key]?.files || [];
                    if (Object.keys(vals).length === 0) return null;
                    return (
                      <Paper
                        key={sec.key}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700 }}
                          >
                            {sec.title}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            {files.map((fn, i) => (
                              <Chip
                                key={`${sec.key}-f-${i}`}
                                size="small"
                                label={fn}
                              />
                            ))}
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1,
                          }}
                        >
                          {Object.entries(vals).map(([k, v]) => (
                            <React.Fragment key={`${sec.key}-${k}`}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {k}
                              </Typography>
                              <Typography variant="body2">
                                {String(v)}
                              </Typography>
                            </React.Fragment>
                          ))}
                        </Box>
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() =>
                              handleApply(getValuesForDocType(sec.key))
                            }
                          >
                            Aplică doar {sec.title}
                          </Button>
                        </Box>
                      </Paper>
                    );
                  });
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreviewOpen(false);
              setDetectedMeta(null);
            }}
          >
            Închide
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={
              working || !detected || Object.keys(detected).length === 0
            }
          >
            Aplică în JIRA
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
